import React, { useState, useEffect, useRef, useCallback } from "react";
import { AIActor } from "../types/actor";
import {
  PlayIcon,
  PauseIcon,
  PencilIcon,
  PlusIcon,
} from "@heroicons/react/24/solid";
import api from "../services/api";
import WavesurferPlayer from "@wavesurfer/react";

// Add CSS variables for dark mode support
const wavesurferStyle = `
  :root {
    --wave-color: #E5E7EB;
    --progress-color: #2754CC;
  }

  .dark {
    --wave-color: #4B5563;
    --progress-color: #FFFFFF;
  }
`;

interface AudioSettingsProps {
  selectedActors: AIActor[];
  projectId: string;
  onAddActor: () => void;
}

interface VoiceSettings {
  id?: string;
  actor_id: string;
  model_id: string;
  stability: number;
  similarity: number;
  exaggeration: number;
  use_speaker_boost?: boolean;
  voice_over?: string;
}

interface VoiceDemo {
  id: string;
  voice_id: string;
  voice_name: string;
  audio_url: string;
  created_at: string;
}

const DEFAULT_SETTINGS = {
  model_id: "eleven_monolingual_v1",
  stability: 0.5,
  similarity: 0.75,
  exaggeration: 0.0,
  use_speaker_boost: true,
};

// Default audio URL as fallback if no demo is found
const DEFAULT_AUDIO_URL = "https://img.ugz.ai/voice_demos/default_demo.mp3";

// Individual Character Audio Player Component
interface CharacterAudioPlayerProps {
  actor: AIActor;
  index: number;
  url: string;
  onSelect: () => void;
  isSelected: boolean;
}

function CharacterAudioPlayer({
  actor,
  index,
  url,
  onSelect,
  isSelected,
}: CharacterAudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [wavesurfer, setWavesurfer] = useState<any>(null);

  // Handle wavesurfer events
  const handleReady = (ws: any) => {
    setWavesurfer(ws);
    setDuration(ws.getDuration());
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleTimeUpdate = (ws: any) => {
    setCurrentTime(ws.getCurrentTime());
  };

  const handleFinish = () => {
    setIsPlaying(false);
  };

  // Play/pause toggle
  const togglePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (wavesurfer) {
      wavesurfer.playPause();
    }
  };

  // Format time display
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div
      className={`p-3 rounded-lg cursor-pointer transition-all duration-200 h-23 relative pt-4 ${
        isSelected
          ? " bg-white dark:bg-gray-800 border-2 border-purple-500"
          : "bg-white dark:bg-gray-800 border-2 border-transparent hover:border-purple-300"
      }`}
      onClick={onSelect}
      // style={{ height: "130px", overflow: "hidden" }}
    >
      <div className="flex justify-end absolute top-1.5 right-1.5">
        <button
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          onClick={(e) => e.stopPropagation()}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div className="flex items-center gap-3">
        {/* Left: Actor image with play button on hover */}
        <div className="flex-shrink-0 relative group">
          <img
            src={actor.image}
            alt={`avatar`}
            className="w-10 h-10 rounded-full object-cover border border-gray-200 dark:border-gray-700"
          />
          <button
            onClick={togglePlayPause}
            className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
          >
            {isPlaying ? (
              <PauseIcon className="h-5 w-5 text-white" />
            ) : (
              <PlayIcon className="h-5 w-5 text-white" />
            )}
          </button>
        </div>

        {/* Middle: Waveform and text in one line */}
        <div className="flex-1 flex flex-col justify-center">
          {/* Waveform player */}
          <div
            className="w-full rounded-lg overflow-hidden cursor-pointer bg-white dark:bg-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            <WavesurferPlayer
              height={35}
              waveColor="var(--wave-color, #E5E7EB)"
              progressColor="var(--progress-color, #2754CC)"
              cursorColor="transparent"
              barWidth={2}
              barGap={1}
              barRadius={2}
              url={url}
              onReady={handleReady}
              onPlay={handlePlay}
              onPause={handlePause}
              onAudioprocess={handleTimeUpdate}
              onSeeking={handleTimeUpdate}
              onFinish={handleFinish}
            />
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs text-gray-600 dark:text-gray-400">
              {formatTime(0)}
            </span>
            <span className="text-xs text-gray-600 dark:text-gray-400">
              {formatTime(currentTime)}
            </span>
          </div>
        </div>

        {/* Right: Edit button */}
        <div className="flex-shrink-0 flex items-center">
          <button
            className="p-1.5 rounded-full bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:cursor-pointer"
            onClick={(e) => {
              // Don't stop propagation, allowing the click to bubble up to the parent div
              onSelect(); // Call the onSelect function that's passed to CharacterAudioPlayer
            }}
          >
            <PencilIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AudioSettings({
  selectedActors,
  projectId,
  onAddActor,
}: AudioSettingsProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(30); // Will be updated when audio loads
  const [voiceSettings, setVoiceSettings] = useState<
    Record<string, VoiceSettings>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<
    Record<string, "idle" | "saving" | "saved" | "error">
  >({});
  const [selectedActor, setSelectedActor] = useState<string | null>(null);
  const [actorPlayingState, setActorPlayingState] = useState<
    Record<string, boolean>
  >({});
  const [actorCurrentTimes, setActorCurrentTimes] = useState<
    Record<string, number>
  >({});
  const [voiceDemos, setVoiceDemos] = useState<Record<string, VoiceDemo>>({});
  const [actorAudioUrls, setActorAudioUrls] = useState<Record<string, string>>(
    {}
  );
  // Add state to track selected voices for each actor
  const [selectedVoices, setSelectedVoices] = useState<Record<string, string>>(
    {}
  );

  // Wavesurfer instance
  const [wavesurfer, setWavesurfer] = useState<any>(null);

  // Audio reference
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Debounce and save handling
  const debouncesRef = useRef<Record<string, NodeJS.Timeout>>({});

  // Wavesurfer event handlers
  const handleWavesurferReady = (ws: any) => {
    setWavesurfer(ws);
    setDuration(ws.getDuration());
  };

  const handleWavesurferPlay = () => {
    setIsPlaying(true);

    // Update all actors' playing state
    const newPlayingStates: Record<string, boolean> = {};
    selectedActors.forEach((actor) => {
      newPlayingStates[actor.id] = true;
    });
    setActorPlayingState((prev) => ({ ...prev, ...newPlayingStates }));
  };

  const handleWavesurferPause = () => {
    setIsPlaying(false);

    // Update all actors' playing state
    const newPlayingStates: Record<string, boolean> = {};
    selectedActors.forEach((actor) => {
      newPlayingStates[actor.id] = false;
    });
    setActorPlayingState((prev) => ({ ...prev, ...newPlayingStates }));
  };

  const handleWavesurferTimeUpdate = (ws: any) => {
    const time = ws.getCurrentTime();
    setCurrentTime(time);

    // Sync all actor times
    const newTimes: Record<string, number> = {};
    selectedActors.forEach((actor) => {
      newTimes[actor.id] = time;
    });
    setActorCurrentTimes((prev) => ({ ...prev, ...newTimes }));
  };

  const handleWavesurferSeeking = (ws: any) => {
    const time = ws.getCurrentTime();
    setCurrentTime(time);

    // Sync all actor times
    const newTimes: Record<string, number> = {};
    selectedActors.forEach((actor) => {
      newTimes[actor.id] = time;
    });
    setActorCurrentTimes((prev) => ({ ...prev, ...newTimes }));
  };

  const handleWavesurferFinish = () => {
    setIsPlaying(false);

    // Reset all actor playing states
    const newPlayingStates: Record<string, boolean> = {};
    selectedActors.forEach((actor) => {
      newPlayingStates[actor.id] = false;
    });
    setActorPlayingState((prev) => ({ ...prev, ...newPlayingStates }));
  };

  // Fetch voice demos for all actors
  const fetchVoiceDemos = async () => {
    try {
      // First, get the full list of actors with their voice IDs
      const actorsWithVoiceIds = selectedActors.filter(
        (actor) => actor.voice_id
      );

      if (actorsWithVoiceIds.length === 0) {
        console.log("No actors with voice IDs found");
        return;
      }

      // Fetch voice demos for each actor with a voice_id
      const demoPromises = actorsWithVoiceIds.map(async (actor) => {
        try {
          const response = await api.get(
            `/api/voice/voice-demo/${actor.voice_id}`
          );
          console.log("response voice demo", response.data.demo);
          if (response.data.success && response.data.demo) {
            return {
              actorId: actor.id,
              voiceId: actor.voice_id,
              demo: response.data.demo.audio_url,
            };
          }
        } catch (error) {
          console.error(
            `Error fetching voice demo for actor ${actor.id}:`,
            error
          );
          return null;
        }
      });

      const results = await Promise.all(demoPromises);
      console.log("results", results);
      // Process results
      // const newVoiceDemos: Record<string, VoiceDemo> = {};
      const newAudioUrls: Record<string, string> = {};

      results.filter(Boolean).forEach((result) => {
        if (result && result.demo) {
          // Store the demo by voice ID
          // newVoiceDemos[result.voiceId] = result.demo;
          // Map actor ID to their demo URL
          newAudioUrls[result.voiceId] = result.demo;
        }
      });

      // setVoiceDemos(newVoiceDemos);
      setActorAudioUrls(newAudioUrls);
    } catch (error) {
      console.error("Error fetching voice demos:", error);
    }
  };

  // Create an audio element when the component mounts
  useEffect(() => {
    // Fetch voice demos for selected actors
    fetchVoiceDemos();

    // Initially use default audio
    const initialAudioUrl = DEFAULT_AUDIO_URL;
    audioRef.current = new Audio(initialAudioUrl);

    // Update duration when metadata is loaded
    audioRef.current.addEventListener("loadedmetadata", () => {
      if (audioRef.current) {
        setDuration(audioRef.current.duration);
      }
    });

    // Handle audio ending
    audioRef.current.addEventListener("ended", () => {
      setIsPlaying(false);

      // Reset all actor playing states
      if (selectedActors.length > 0) {
        const newPlayingStates: Record<string, boolean> = {};
        selectedActors.forEach((actor) => {
          newPlayingStates[actor.id] = false;
        });
        setActorPlayingState((prev) => ({ ...prev, ...newPlayingStates }));
      }
    });

    // Cleanup
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
    };
  }, [selectedActors]);

  // Set the first actor as selected initially
  useEffect(() => {
    if (selectedActors.length > 0 && !selectedActor) {
      setSelectedActor(selectedActors[0].id);
    }
  }, [selectedActors, selectedActor]);

  // Initialize selected voices from actors when they change
  useEffect(() => {
    const initialVoices: Record<string, string> = {};

    selectedActors.forEach((actor) => {
      if (actor.voice_id) {
        initialVoices[actor.id] = actor.voice_id;
      }
    });

    setSelectedVoices((prev) => ({
      ...prev,
      ...initialVoices,
    }));
  }, [selectedActors]);

  // Load voice settings from backend
  useEffect(() => {
    const loadVoiceSettings = async () => {
      if (!projectId) return;

      setIsLoading(true);
      try {
        const response = await api.get(`/api/voice-settings/${projectId}`);

        // Convert array to record for easy access by actor ID
        const settings = response.data.voice_settings.reduce(
          (acc: Record<string, VoiceSettings>, setting: VoiceSettings) => {
            acc[setting.actor_id] = setting;
            return acc;
          },
          {}
        );

        setVoiceSettings(settings);

        // Initialize selectedVoices from voice_over settings
        const voiceSelections: Record<string, string> = {};
        response.data.voice_settings.forEach((setting: VoiceSettings) => {
          if (setting.voice_over) {
            voiceSelections[setting.actor_id] = setting.voice_over;
          }
        });

        setSelectedVoices((prevVoices) => ({
          ...prevVoices,
          ...voiceSelections,
        }));
      } catch (error) {
        console.error("Error loading voice settings:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadVoiceSettings();
  }, [projectId]);

  // Initialize settings for any new actors that don't have settings yet
  useEffect(() => {
    const newSettings: Record<string, VoiceSettings> = {};
    let hasNewSettings = false;

    selectedActors.forEach((actor) => {
      if (!voiceSettings[actor.id]) {
        newSettings[actor.id] = {
          actor_id: actor.id,
          ...DEFAULT_SETTINGS,
        };
        hasNewSettings = true;
      }

      // Initialize actor play states
      if (actorPlayingState[actor.id] === undefined) {
        setActorPlayingState((prev) => ({
          ...prev,
          [actor.id]: false,
        }));
      }

      // Initialize actor current times
      if (actorCurrentTimes[actor.id] === undefined) {
        setActorCurrentTimes((prev) => ({
          ...prev,
          [actor.id]: 0,
        }));
      }
    });

    if (hasNewSettings) {
      setVoiceSettings((prev) => ({
        ...prev,
        ...newSettings,
      }));
    }
  }, [selectedActors, voiceSettings, actorPlayingState, actorCurrentTimes]);

  // Save settings to the backend with debounce
  const saveSettings = useCallback(
    async (actorId: string, settings: VoiceSettings) => {
      if (!projectId) return;

      // Clear any existing debounce for this actor
      if (debouncesRef.current[actorId]) {
        clearTimeout(debouncesRef.current[actorId]);
      }

      // Set a debounce timeout
      debouncesRef.current[actorId] = setTimeout(async () => {
        setSaveStatus((prev) => ({ ...prev, [actorId]: "saving" }));

        try {
          await api.post(
            `/api/voice-settings/${projectId}/${actorId}`,
            settings
          );
          setSaveStatus((prev) => ({ ...prev, [actorId]: "saved" }));

          // Reset to idle after a delay
          setTimeout(() => {
            setSaveStatus((prev) => ({ ...prev, [actorId]: "idle" }));
          }, 1500);
        } catch (error) {
          console.error(
            `Error saving voice settings for actor ${actorId}:`,
            error
          );
          setSaveStatus((prev) => ({ ...prev, [actorId]: "error" }));
        }
      }, 800);
    },
    [projectId]
  );

  // Handle slider changes
  const handleSettingChange = useCallback(
    (actorId: string, setting: keyof VoiceSettings, value: number) => {
      // Update the state
      setVoiceSettings((prev) => {
        const currentSettings = prev[actorId] || {
          actor_id: actorId,
          ...DEFAULT_SETTINGS,
        };

        const updatedSettings = {
          ...currentSettings,
          [setting]: value,
        };

        // Schedule a save to the backend
        saveSettings(actorId, updatedSettings);

        return {
          ...prev,
          [actorId]: updatedSettings,
        };
      });
    },
    [saveSettings]
  );

  // Handle speaker boost toggle
  const handleSpeakerBoostChange = useCallback(
    (actorId: string, checked: boolean) => {
      setVoiceSettings((prev) => {
        const currentSettings = prev[actorId] || {
          actor_id: actorId,
          ...DEFAULT_SETTINGS,
        };

        const updatedSettings = {
          ...currentSettings,
          use_speaker_boost: checked,
        };

        // Schedule a save to the backend
        saveSettings(actorId, updatedSettings);

        return {
          ...prev,
          [actorId]: updatedSettings,
        };
      });
    },
    [saveSettings]
  );

  // Delete voice settings when actor is removed
  useEffect(() => {
    const actorIds = selectedActors.map((actor) => actor.id);

    Object.keys(voiceSettings).forEach((actorId) => {
      // If settings exist for an actor that's no longer selected
      if (!actorIds.includes(actorId)) {
        // Cancel any pending saves
        if (debouncesRef.current[actorId]) {
          clearTimeout(debouncesRef.current[actorId]);
          delete debouncesRef.current[actorId];
        }

        // Delete the settings from backend
        const deleteSettings = async () => {
          try {
            await api.delete(`/api/voice-settings/${projectId}/${actorId}`);

            // Remove from local state
            setVoiceSettings((prev) => {
              const newSettings = { ...prev };
              delete newSettings[actorId];
              return newSettings;
            });

            // Clean up actor play states
            setActorPlayingState((prev) => {
              const newState = { ...prev };
              delete newState[actorId];
              return newState;
            });

            // Clean up actor current times
            setActorCurrentTimes((prev) => {
              const newTimes = { ...prev };
              delete newTimes[actorId];
              return newTimes;
            });
          } catch (error) {
            console.error(
              `Error deleting voice settings for actor ${actorId}:`,
              error
            );
          }
        };

        deleteSettings();
      }
    });
  }, [selectedActors, projectId, voiceSettings]);

  // Audio player controls
  const handlePlayPause = () => {
    if (!wavesurfer) return;

    wavesurfer.playPause();
  };

  const handleTimeUpdate = (value: number) => {
    if (!wavesurfer) return;

    wavesurfer.seekTo(value / duration);
    setCurrentTime(value);

    // Update all actor times to keep them synchronized
    const newTimes: Record<string, number> = {};
    selectedActors.forEach((actor) => {
      newTimes[actor.id] = value;
    });
    setActorCurrentTimes((prev) => ({ ...prev, ...newTimes }));
  };

  // Actor-specific play controls
  const handleActorPlayPause = (actorId: string) => {
    if (!wavesurfer) return;

    if (!isPlaying) {
      wavesurfer.play();
    } else {
      wavesurfer.pause();
    }
  };

  const handleActorTimeUpdate = (actorId: string, value: number) => {
    if (!wavesurfer) return;

    // Set the wavesurfer time
    wavesurfer.seekTo(value / duration);

    // Update all actor times to keep them synchronized
    const newTimes: Record<string, number> = {};
    selectedActors.forEach((actor) => {
      newTimes[actor.id] = value;
    });
    setActorCurrentTimes((prev) => ({ ...prev, ...newTimes }));
    setCurrentTime(value);
  };

  // Format time display
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Helper to get save status indicator
  const getSaveStatusIndicator = (actorId: string) => {
    const status = saveStatus[actorId];
    if (status === "saving")
      return <span className="text-xs text-yellow-500">Saving...</span>;
    if (status === "saved")
      return <span className="text-xs text-green-500">Saved</span>;
    if (status === "error")
      return <span className="text-xs text-red-500">Error</span>;
    return null;
  };

  // Helper to get the correct audio URL for an actor
  const getActorAudioUrl = (actor: AIActor): string => {
    const voiceIds = [
      "dutch_girl_1",
      "danish_girl_1",
      "nz_girl_1",
      "nz_girl_2",
      "nz_boy_1",
      "romanian_boy_1",
      "french_girl_1",
      "polish_girl_1",
    ];

    if (
      selectedVoices[actor.id] &&
      voiceIds.includes(selectedVoices[actor.id])
    ) {
      return `https://img.ugz.ai/${selectedVoices[actor.id]}.mp3`;
    }

    // If we have a cached audio URL for this actor, use it
    if (actorAudioUrls[actor.voice_id]) {
      return actorAudioUrls[actor.voice_id];
    }

    // If the actor has a voice_id and we have a demo for it, use that
    if (actor.voice_id && voiceDemos[actor.voice_id]) {
      return voiceDemos[actor.voice_id].audio_url;
    }

    // Fallback to default
    return DEFAULT_AUDIO_URL;
  };

  // Find the currently selected actor object
  const currentActor = selectedActors.find(
    (actor) => actor.id === selectedActor
  );

  // Get settings for the selected actor
  const currentSettings = selectedActor
    ? voiceSettings[selectedActor] || {
        actor_id: selectedActor,
        ...DEFAULT_SETTINGS,
      }
    : null;

  // Get the correct audio URL for the current actor
  const currentActorAudioUrl = currentActor
    ? getActorAudioUrl(currentActor)
    : DEFAULT_AUDIO_URL;

  // Handle voice change
  const handleVoiceChange = async (actorId: string, voiceId: string) => {
    // Update local state
    setSelectedVoices((prev) => ({
      ...prev,
      [actorId]: voiceId,
    }));

    // Update save status
    setSaveStatus((prev) => ({ ...prev, [actorId]: "saving" }));

    try {
      // Update the voice settings through the API
      const voiceSettingsData = {
        voice_over: voiceId === "Default" ? null : voiceId,
      };

      // Send the updated voice setting to the voice-settings API
      await api.post(
        `/api/voice-settings/${projectId}/${actorId}`,
        voiceSettingsData
      );

      console.log(`Updated voice-over for actor ${actorId} to ${voiceId}`);

      // Refresh voice demos to get the updated audio sample
      if (voiceId !== "Default") {
        try {
          const response = await api.get(`/api/voice/voice-demo/${voiceId}`);
          if (response.data.success && response.data.demo) {
            setActorAudioUrls((prev) => ({
              ...prev,
              [voiceId]: response.data.demo.audio_url,
            }));
          }
        } catch (error) {
          console.error(`Error fetching voice demo for ${voiceId}:`, error);
        }
      }

      // Show saved status
      setSaveStatus((prev) => ({ ...prev, [actorId]: "saved" }));

      // Reset to idle after a delay
      setTimeout(() => {
        setSaveStatus((prev) => ({ ...prev, [actorId]: "idle" }));
      }, 1500);
    } catch (error) {
      console.error(`Error updating voice-over for actor ${actorId}:`, error);
      setSaveStatus((prev) => ({ ...prev, [actorId]: "error" }));
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 transition-colors duration-200">
      <style dangerouslySetInnerHTML={{ __html: wavesurferStyle }} />
      <h2 className="text-xl font-semibold mb-6 text-gray-900 dark:text-white">
        Audio Settings
      </h2>

      {/* Two-column layout for voice settings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        {/* Left column: Actor selection */}
        <div className="md:col-span-1 grid grid-cols-2 gap-4 content-start">
          {isLoading ? (
            <div className="p-4 text-center col-span-2">
              <div className="animate-pulse w-6 h-6 rounded-full bg-gray-300 dark:bg-gray-700 mx-auto mb-2"></div>
              <p className="text-gray-500 dark:text-gray-400">Loading...</p>
            </div>
          ) : (
            <>
              {selectedActors.map((actor, index) => (
                <CharacterAudioPlayer
                  key={actor.id}
                  actor={actor}
                  index={index}
                  url={getActorAudioUrl(actor)}
                  onSelect={() => setSelectedActor(actor.id)}
                  isSelected={selectedActor === actor.id}
                />
              ))}
              {/* Add button for new actor */}
              <div className="h-23 w-full flex items-center justify-center mx-auto">
                <button
                  onClick={onAddActor}
                  className="h-17 w-17 bg-gradient-to-b from-purple-600 to-purple-400 rounded-xl border-3 border-b-4 border-purple-800 flex items-center justify-center hover:cursor-pointer"
                >
                  <div className="h-8 w-8 mb-1 fill-white">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 448 512"
                    >
                      {/*!Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.*/}
                      <path d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 144L48 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l144 0 0 144c0 17.7 14.3 32 32 32s32-14.3 32-32l0-144 144 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-144 0 0-144z" />
                    </svg>
                  </div>
                </button>
              </div>
            </>
          )}
        </div>

        {/* Right column: Settings for selected actor */}
        <div className="">
          {isLoading ? (
            <div className="p-4 text-center">
              <div className="animate-pulse w-6 h-6 rounded-full bg-gray-300 dark:bg-gray-700 mx-auto mb-2"></div>
              <p className="text-gray-500 dark:text-gray-400">
                Loading settings...
              </p>
            </div>
          ) : currentActor && currentSettings ? (
            <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <div className="mb-6">
                <div className="flex items-start gap-4 relative">
                  {/* Actor image with play button overlay */}
                  <div className="relative flex-shrink-0">
                    <img
                      src={currentActor.image}
                      alt={`Actor ${
                        selectedActors.findIndex(
                          (a) => a.id === currentActor.id
                        ) + 1
                      }`}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <button
                      onClick={() => {
                        if (wavesurfer) {
                          wavesurfer.playPause();
                        }
                      }}
                      className="absolute m-2.5 rounded-full inset-0 flex items-center justify-center bg-blue-500/70 rounded-fulltransition-opacity hover:cursor-pointer"
                    >
                      {isPlaying ? (
                        <PauseIcon className="h-8 w-8 text-white" />
                      ) : (
                        <PlayIcon className="h-8 w-8 text-white" />
                      )}
                    </button>
                  </div>
                  {/* Audio player and actor info */}
                  <div className="flex-1">
                    <h3 className="font-normal text-gray-900 dark:text-white">
                      Actor{" "}
                      {selectedActors.findIndex(
                        (a) => a.id === currentActor.id
                      ) + 1}
                    </h3>

                    {/* Audio scrubber */}
                    <div className="flex flex-col mb-3">
                      <div className="flex-1">
                        <input
                          type="range"
                          min={0}
                          max={duration || 30}
                          step={0.01}
                          value={currentTime}
                          onChange={(e) => {
                            const newTime = Number(e.target.value);
                            setCurrentTime(newTime);
                            if (wavesurfer) {
                              wavesurfer.seekTo(newTime / duration);
                            }
                          }}
                          className="w-full accent-blue-500"
                        />
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">0:00</span>
                        <span className="text-xs text-gray-500">
                          {formatTime(duration)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="absolute right-0 top-0">
                    {getSaveStatusIndicator(currentActor.id)}
                  </div>
                </div>

                {/* Hidden wavesurfer player for actual audio playback */}
                <div className="hidden">
                  <WavesurferPlayer
                    height={0}
                    waveColor="var(--wave-color, #E5E7EB)"
                    progressColor="var(--progress-color, #2754CC)"
                    cursorColor="transparent"
                    barWidth={0}
                    barGap={0}
                    barRadius={0}
                    url={currentActorAudioUrl}
                    onReady={handleWavesurferReady}
                    onPlay={handleWavesurferPlay}
                    onPause={handleWavesurferPause}
                    onAudioprocess={handleWavesurferTimeUpdate}
                    onSeeking={handleWavesurferSeeking}
                    onFinish={handleWavesurferFinish}
                  />
                </div>
              </div>

              {/* Voice settings sliders */}
              <div className="space-y-3">
                {/* Grid layout for all settings */}
                <div className="grid grid-cols-4 gap-2">
                  {/* Voice-Over dropdown */}
                  <div className="col-span-1">
                    <label className="text-sm text-gray-600 dark:text-gray-400">
                      Voice-Over
                    </label>
                  </div>
                  <div className="col-span-3">
                    <div className="relative flex items-center">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-gray-500"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <select
                        className={`w-full text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md pl-10 pr-10 py-1 appearance-none ${
                          saveStatus[currentActor.id] === "saving"
                            ? "opacity-75 cursor-wait"
                            : ""
                        }`}
                        value={selectedVoices[currentActor.id] || "Default"}
                        style={{ backgroundPosition: "right 0.5rem center" }}
                        onChange={(e) => {
                          handleVoiceChange(currentActor.id, e.target.value);
                        }}
                        disabled={saveStatus[currentActor.id] === "saving"}
                      >
                        <option value="Default">Default Voice</option>
                        <option value="dutch_girl_1">Dutch Girl 1</option>
                        <option value="danish_girl_1">Danish Girl 1</option>
                        <option value="nz_girl_1">New Zealand Girl 1</option>
                        <option value="nz_girl_2">New Zealand Girl 2</option>
                        <option value="nz_boy_1">New Zealand Guy 1</option>
                        <option value="romanian_boy_1">Romanian Guy 1</option>
                        <option value="french_girl_1">French Girl 1</option>
                        <option value="polish_girl_1">Polish Girl 1</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-gray-500"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      {/* <div className="ml-2 flex-shrink-0 min-w-[60px]">
                        {saveStatus[currentActor.id] === "saving" && (
                          <span className="text-xs text-yellow-500">
                            Saving...
                          </span>
                        )}
                        {saveStatus[currentActor.id] === "saved" && (
                          <span className="text-xs text-green-500">Saved</span>
                        )}
                        {saveStatus[currentActor.id] === "error" && (
                          <span className="text-xs text-red-500">Error</span>
                        )}
                      </div> */}
                    </div>
                  </div>

                  {/* Speed slider */}
                  <div className="col-span-1">
                    <label className="text-sm text-gray-600 dark:text-gray-400">
                      Speed (1.00x)
                    </label>
                  </div>
                  <div className="col-span-3">
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.01}
                      value={currentSettings.stability}
                      onChange={(e) =>
                        handleSettingChange(
                          currentActor.id,
                          "stability",
                          Number(e.target.value)
                        )
                      }
                      className="w-full accent-blue-500"
                    />
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Slow</span>
                      <span className="text-xs text-gray-500">Fast</span>
                    </div>
                  </div>

                  {/* Stability slider */}
                  <div className="col-span-1">
                    <label className="text-sm text-gray-600 dark:text-gray-400">
                      Stability (0.55)
                    </label>
                  </div>
                  <div className="col-span-3">
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.01}
                      value={currentSettings.similarity}
                      onChange={(e) =>
                        handleSettingChange(
                          currentActor.id,
                          "similarity",
                          Number(e.target.value)
                        )
                      }
                      className="w-full accent-blue-500"
                    />
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">
                        More Variable
                      </span>
                      <span className="text-xs text-gray-500">More Stable</span>
                    </div>
                  </div>

                  {/* Clarity + Similarity slider */}
                  <div className="col-span-1">
                    <label className="text-sm text-gray-600 dark:text-gray-400">
                      Clarity + Similarity
                    </label>
                  </div>
                  <div className="col-span-3">
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.01}
                      value={currentSettings.exaggeration}
                      onChange={(e) =>
                        handleSettingChange(
                          currentActor.id,
                          "exaggeration",
                          Number(e.target.value)
                        )
                      }
                      className="w-full accent-blue-500"
                    />
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Low</span>
                      <span className="text-xs text-gray-500">High</span>
                    </div>
                  </div>

                  {/* Speaker Boost checkbox and Reset Settings in one row */}
                  <div className="col-span-1">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`speaker-boost-${currentActor.id}`}
                        className="h-4 w-4 text-blue-500 rounded mr-2"
                        checked={currentSettings.use_speaker_boost}
                        onChange={(e) =>
                          handleSpeakerBoostChange(
                            currentActor.id,
                            e.target.checked
                          )
                        }
                      />
                      <label
                        htmlFor={`speaker-boost-${currentActor.id}`}
                        className="text-sm text-gray-600 dark:text-gray-400"
                      >
                        Speaker Boost
                      </label>
                    </div>
                  </div>
                  <div className="col-span-3 flex justify-end">
                    <button
                      className="px-2 bg-white border border-gray-300 rounded-md text-xs text-gray-600 hover:bg-gray-50 hover:cursor-pointer"
                      onClick={() => {
                        if (selectedActor) {
                          const defaultSettings = {
                            actor_id: selectedActor,
                            ...DEFAULT_SETTINGS,
                          };
                          setVoiceSettings((prev) => ({
                            ...prev,
                            [selectedActor]: defaultSettings,
                          }));
                          saveSettings(selectedActor, defaultSettings);
                        }
                      }}
                    >
                      Reset Settings
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-gray-500 dark:text-gray-400">
                Select a character to configure voice settings
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
